// The addresses of an app's own page (ADR 0058 §7): it serves one app, so its pages sit at the root of its origin.
// The home app owns the address bar: its route is the path (`/templates` for its route `/templates`, `/` for its first
// page; the earlier `/?<route>` leads there). Another app at /apps/<app> (its route in the query), a companion page at
// /<page>, the sign-in page at /login, the person's sign-in security at /account/security. Nothing else is in an
// address.

const enc = encodeURIComponent;

/** A page's address: `/<page>` (`workflows`, `models/m1/run`); '' is the home app's. */
export function pagePath(page: string): string {
  const clean = page.replace(/^\/+/, '').replace(/\/+$/, '');
  return clean ? `/${clean}` : '/';
}

/** An app's address: / for the home app, /apps/<app> for another. */
export function appPath(app: string, home: string | null): string {
  return app === home ? '/' : `/apps/${enc(app)}`;
}

/**
 * A path written for the address of the host where the project is authored, whose own segments are placeholders
 * (`/x/{slug}/y/{name}/workflows`): this page's own path is what follows the last placeholder (`/workflows`). A path
 * without placeholders is this page's already.
 */
export function ownPath(path: string): string {
  const segments = path.split('/');
  let last = -1;
  segments.forEach((s, i) => {
    if (/^\{[A-Za-z_]+\}$/.test(s)) last = i;
  });
  return last < 0 ? path : pagePath(segments.slice(last + 1).join('/'));
}

/** The address a page is shown at: the home app's and the apps list's addresses as /. */
export function shownPath(pathname: string, w: { home: string | null }): string {
  if (pathname === '/apps' || pathname === '/apps/') return '/';
  if (w.home && pathname === `/apps/${enc(w.home)}`) return '/';
  return pathname;
}

/** The address of the home app's route (`/templates`; its first page, `/`). */
export const pathOfRoute = (route: string): string =>
  !route || route === '/' ? '/' : route.startsWith('/') ? route : `/${route}`;

/** The home app's route at an address (`/templates?x=1` → `/templates?x=1`; `/` → its first page, ''). */
export const routeOfPath = (pathname: string, search: string): string =>
  pathname === '/' && !search ? '' : `${pathname}${search}`;

/**
 * The home app's route an earlier address carried in its query (`/?/templates`, as the page wrote it before the home
 * app owned the path), else null: the page leads such an address to the route's own path.
 */
export function legacyRouteOf(pathname: string, search: string): string | null {
  if (pathname !== '/' || !search.startsWith('?')) return null;
  let route: string;
  try {
    route = decodeURIComponent(search.slice(1));
  } catch {
    return null;
  }
  return route.startsWith('/') ? route : null;
}
